import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type Lang = "sv" | "en";
const STORAGE_KEY = "nrativ-marketing-lang";

interface LangContext {
  lang: Lang;
  setLang: (l: Lang) => void;
  /** Språkväljare utan pärmen tur & retur mellan objekt och strängar. */
  t: <T>(pair: { sv: T; en: T }) => T;
}

const Ctx = createContext<LangContext | undefined>(undefined);

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "sv";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "sv" || stored === "en") return stored;
    /** Fall back to browser default: treat anything non-English as Swedish. */
    const nav = window.navigator.language?.toLowerCase() ?? "";
    return nav.startsWith("en") ? "en" : "sv";
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const value = useMemo<LangContext>(
    () => ({
      lang,
      setLang,
      t: ({ sv, en }) => (lang === "en" ? en : sv),
    }),
    [lang, setLang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLang must be used within <LangProvider>");
  return ctx;
}
